import React from 'react';
import { Paper, Grid, Typography, Skeleton } from '@mui/material';
import styled from '@emotion/styled';
import TitleTypeLabel from '../_common/TitleTypeLabel';
import { selectTitle, selectTitleIsSeries, selectTitleLoading, selectTitleSeasonsObject } from '@/redux/selectors/titleSelectors';
import TitleDetailSeriesBrowser from './TitleDetailSeriesBrowser/TitleDetailSeriesBrowser';
import { insertWordBreaks, pluralizeNoun } from '@/_utils/utils';
import { useQuery } from '../_hooks/useQuery';
import { LabelValue } from '../_common';
import { selectTitleTypes } from '@/redux/selectors/constantsSelectors';
import { useAppSelector } from '@/redux/hooks';
import SizePreservingLoader from '@/components/_common/SizePreservingLoader/SizePreservingLoader';

const TitleDetailHeader = () => {
  const titleMap = useAppSelector(selectTitleTypes);
  const seasons = useAppSelector(selectTitleSeasonsObject);
  const title = useAppSelector(selectTitle);
  const isLoading = useAppSelector(selectTitleLoading);
  const isSeries = useAppSelector(selectTitleIsSeries);

  const query = useQuery();
  const seasonId = query.get('season_id');
  const episodeId = query.get('episode_id');
  const supplementalId = query.get('supplemental_id');

  const pageType = supplementalId ? 'supplemental' : episodeId ? 'episode' : seasonId ? 'season' : 'title';

  let entity;

  const getEndDate = () => {
    if (!isSeries) return;
    if (!title?.end_year)
      return (
        <span style={{ paddingLeft: '.5rem' }}>
          - <span style={{ opacity: 0.5 }}>present</span>
        </span>
      );

    return `- ${title.end_year}`;
  };

  if(pageType === 'supplemental') {
    entity = seasonId ? seasons[seasonId]?.children?.find((ep: any) => ep.id == supplementalId) || {} : {};
  } else if (pageType === 'episode') {
    entity = seasonId ? seasons[seasonId]?.children?.find((ep: any) => ep.id == episodeId) || {} : {};
  } else if (pageType === 'season') {
    entity = title?.children?.find((season: any) => season.id == seasonId) || {};
  } else entity = title;

  const isParent = pageType === 'title';
  const pageData = {
    name: entity?.name,
    entity_type: isParent ? title?.entity_type : entity?.entity_type,
    available_count: entity?.children?.length
  };

  const showAvailableVersions = entity?.children?.[0]?.entity_type === 'version';
  const showAvailableCount = !showAvailableVersions && entity?.children?.length;

  const childLabel = () => {
    switch (pageData.entity_type) {
      case 'series':
        return pluralizeNoun(titleMap['season']);
      case 'season':
        return pluralizeNoun(titleMap['episode']);
      case 'campaign':
        return pluralizeNoun(titleMap['marketing']);
      case 'marketing':
        return 'Children';
      default:
        return 'Children';
    }
  };

  return (
    <Paper sx={{ py: 2, px: 3 }}>
      <Grid container alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h2" sx={{ fontWeight: 300 }}>
          <SizePreservingLoader
            isLoading={isLoading}
            loader={
              <div>
                <Skeleton width={280} />
              </div>
            }
          >
            {pageData.name && insertWordBreaks(pageData.name)}
          </SizePreservingLoader>
        </Typography>
        {!isLoading && pageType === 'title' && title?.start_year && (
          <Typography variant="h6" sx={{ opacity: 0.6, ml: 1 }}>
            ({title.start_year}
            {getEndDate()})
          </Typography>
        )}
      </Grid>
      <SizePreservingLoader isLoading={isLoading} loader={<Skeleton width={200} />}>
        <Grid container alignItems="center">
          <TitleTypeLabel type={pageData.entity_type} width="auto" />
          {/* only show available count for series (# of seasons) and seasons (# of episodes) */}
          {!!showAvailableCount && (
            <StyledLabelValue color="default" label={`Available ${childLabel()}`} horizontal>
              <strong>{pageData.available_count}</strong>
            </StyledLabelValue>
          )}
        </Grid>
      </SizePreservingLoader>
      {isSeries && <TitleDetailSeriesBrowser />}
    </Paper>
  );
};

export default TitleDetailHeader;

const StyledLabelValue = styled(LabelValue)`
  margin-right: 20px;
`;
